/**
 * McqChoices — renders multiple-choice options as radio buttons.
 *
 * Displays answer choices (A, B, C, D...) as selectable cards.
 * The selected choice text is passed back to the parent as the answer.
 */

import { MathText } from "./Math";

interface McqChoicesProps {
  choices: string[];
  selected: string;
  onSelect: (choice: string) => void;
  disabled?: boolean;
}

export function McqChoices({ choices, selected, onSelect, disabled }: McqChoicesProps) {
  return (
    <div className="mcq-choices" role="radiogroup" aria-label="Answer options">
      {choices.map((choice, i) => {
        const letter = String.fromCharCode(65 + i); // A, B, C, D...
        // If the choice already starts with a letter prefix (e.g. "A. ..."), use it as-is
        const hasPrefix = /^[A-Z][.)]\s/.test(choice);
        const display = hasPrefix ? choice : `${letter}. ${choice}`;
        const value = hasPrefix ? choice : `${letter}. ${choice}`;
        const isSelected = selected === value;

        return (
          <label
            key={i}
            className={`mcq-option ${isSelected ? "mcq-option-selected" : ""} ${disabled ? "mcq-option-disabled" : ""}`}
          >
            <input
              type="radio"
              name="mcq-answer"
              value={value}
              checked={isSelected}
              onChange={() => onSelect(value)}
              disabled={disabled}
              className="mcq-radio"
            />
            <span className="mcq-letter">{hasPrefix ? choice[0] : letter}</span>
            <span className="mcq-text"><MathText inline content={hasPrefix ? choice.slice(3) : choice} /></span>
          </label>
        );
      })}
    </div>
  );
}
